import {inject, Injectable, signal, WritableSignal} from '@angular/core';
import {Experiment, ExperimentClient} from '@amplitude/experiment-js-client';
import {PlatformService} from "./platform.service";
import {createIs} from "typia";

export const lotteryCarouselOptions = ['initial', 'animated'] as const;
export const priceOptions = ['regular-monthly', 'promotion-monthly', '15-monthly', '10-monthly', '20-monthly'] as const;
export const freeTrialOptions = ['14-days', '7-days'] as const;
export const reviewsStyles = ['initial', 'kris'] as const;
export const reviewPromptOptions = ['none', 'dialog'] as const;
export const appIntroductionOptions = ['eligible-page', "none"] as const;
export const backtrackTitles = ['default', 'friendly'] as const;
export const stripeABGroups = ['a', 'b'] as const;
export const paymentLocationGroups = ['original', 'sunk-cost', 'sunk-cost-no-trial'] as const;
export const nativePaymentOptions = ['revenuecat', 'stripe'] as const;
export const resubscribeFlowOptions = ['dialog', 'direct'] as const;

interface AdvancedBenefitsPageInput {
  titleOverride?: string;
}

type AdvancedBenefitsPage = Required<AdvancedBenefitsPageInput>;

const defaultAdvancedBenefitsPage: AdvancedBenefitsPage = {
  titleOverride: '',
};

interface BenefitsPageContentInput {
  activateButtonLabel?: string;
  claimTrialButtonLabel?: string;
  cancelFooter?: string;
  howItWorksTitle?: string;
  freeTrialDescription?: string;
}

type BenefitsPageContent = Required<BenefitsPageContentInput>;

const defaultBenefitsPageContent: BenefitsPageContent = {
  activateButtonLabel: 'Activate Homey',
  claimTrialButtonLabel: 'Claim my free trial',
  cancelFooter: 'Cancel anytime in settings.',
  howItWorksTitle: 'How your free trial works',
  freeTrialDescription: 'Try it out now to have a chance at winning your dream NYC home.',
};
type PriceOption = typeof priceOptions[number];
type FreeTrialOption = typeof freeTrialOptions[number];
type ReviewsStyle = typeof reviewsStyles[number];
type ReviewPrompt = typeof reviewPromptOptions[number];
type AppIntroduction = typeof appIntroductionOptions[number];
type LotteryCarousel = typeof lotteryCarouselOptions[number];
type BacktrackTitle = typeof backtrackTitles[number];
type StripeABGroup = typeof stripeABGroups[number];
type PaymentLocationGroupVariant = typeof paymentLocationGroups[number];
type NativePayment = typeof nativePaymentOptions[number];
export type ResubscribeFlow = typeof resubscribeFlowOptions[number];

interface PromotionHeader {
  text: string;
  link: string;
  color: string;
  enabled: boolean;
}

type EventFeedType = {
  enabled: boolean;
  title: string;
  showLiveIndicator: boolean;
  showEmoji: boolean;
  hideLandingPageImage: boolean;
}

type NativeHeroImageConfig = {
  src: string;
  position: {
    left?: string;
    right?: string;
    top?: string;
    bottom?: string;
  };
  aspectRatio: string;
  width: string;
  height?: string;
};

type NativeLandingPageExperiment = {
  heroImages: NativeHeroImageConfig[];
  title: string;
  subtitle: string;
  buttonText: string;
};

export const nativeLandingPageDefaults: NativeLandingPageExperiment = {
  heroImages: [
    {
      src: '/assets/images/housing-lottery/williamsburg-wharf-a1-photo-0.png',
      position: {
        top: '5rem',
        left: '2rem',
      },
      aspectRatio: '3/2',
      width: '7rem',
    },
    {
      src: '/assets/images/housing-lottery/williamsburg-wharf-a1-photo-5.png',
      position: {
        top: '11.5rem',
        left: '5rem',
      },
      aspectRatio: '3/2',
      width: '6rem',
    },
    {
      src: '/assets/images/housing-lottery/williamsburg-wharf-a1-photo-4.png',
      position: {
        top: '7rem',
        right: '2.5rem',
      },
      aspectRatio: '3/2',
      width: '8rem',
    }
  ],
  title: 'Housing Lottery <br><span class="text-orange-gradient">Made Easy</span>',
  subtitle: 'Auto-apply to all NYC Housing Lotteries<br> in just 7 minutes.',
  buttonText: 'Get started',
};

@Injectable({
  providedIn: 'root'
})
export class ABService {
  public readonly platformService = inject(PlatformService);

  public readonly priceOption = signal<PriceOption>('15-monthly');
  public readonly reviewsStyle = signal<ReviewsStyle>('initial');
  public readonly reviewPrompt = signal<ReviewPrompt>('dialog');
  public readonly freeTrialOption = signal<FreeTrialOption>('7-days');
  public readonly lotteryCarousel = signal<LotteryCarousel>('initial');
  public readonly appIntroduction = signal<AppIntroduction>('none');
  public readonly backtrackTitle = signal<BacktrackTitle>('default');
  public readonly stripeABGroup = signal<StripeABGroup>('a');
  public readonly paymentLocationGroup = signal<PaymentLocationGroupVariant>('original');
  public readonly nativePaymentLocationGroup = signal<PaymentLocationGroupVariant>('original');
  public readonly nativePayment = signal<NativePayment>('revenuecat');
  public readonly resubscribeFlow = signal<ResubscribeFlow>('dialog');
  public readonly advancedBenefitsPage = signal<AdvancedBenefitsPage>(defaultAdvancedBenefitsPage);
  public readonly benefitsPageContent = signal<BenefitsPageContent>(defaultBenefitsPageContent);
  public readonly nativeLandingPage = signal<NativeLandingPageExperiment | undefined>(undefined);
  public readonly promotionHeader = signal<PromotionHeader>({
    text: '',
    link: '/signup/preferences/1',
    color: "blue",
    enabled: false
  });
  public readonly eventFeed = signal<EventFeedType>({
    enabled: false,
    title: 'Recent selections',
    showLiveIndicator: true,
    showEmoji: true,
    hideLandingPageImage: false,
  });
  public readonly basicVariables: { name: string; signal: WritableSignal<any>; options: readonly string[] }[] = [
    {name: 'priceOption', signal: this.priceOption, options: priceOptions},
    {name: 'reviewsStyle', signal: this.reviewsStyle, options: reviewsStyles},
    {name: 'reviewPrompt', signal: this.reviewPrompt, options: reviewPromptOptions},
    {name: 'freeTrialOption', signal: this.freeTrialOption, options: freeTrialOptions},
    {name: 'lotteryCarousel', signal: this.lotteryCarousel, options: lotteryCarouselOptions},
    {name: 'appIntroduction', signal: this.appIntroduction, options: appIntroductionOptions},
    {name: 'backtrackTitle', signal: this.backtrackTitle, options: backtrackTitles},
    {name: 'stripeABGroup', signal: this.stripeABGroup, options: stripeABGroups},
    {name: 'paymentLocationGroup', signal: this.paymentLocationGroup, options: paymentLocationGroups},
    {name: 'nativePaymentLocationGroup', signal: this.nativePaymentLocationGroup, options: paymentLocationGroups},
    {name: 'nativePayment', signal: this.nativePayment, options: nativePaymentOptions},
    {name: 'resubscribeFlow', signal: this.resubscribeFlow, options: resubscribeFlowOptions},
  ];

  public readonly jsonVariables: { name: string; signal: WritableSignal<any>; validate: (input: unknown) => input is any }[] = [
    {name: 'advancedBenefitsPage', signal: this.advancedBenefitsPage, validate: createIs<AdvancedBenefitsPage>()},
    {name: 'benefitsPageContent', signal: this.benefitsPageContent, validate: createIs<BenefitsPageContent>()},
    {name: 'promotionHeader', signal: this.promotionHeader, validate: createIs<PromotionHeader>()},
    {name: 'eventFeed', signal: this.eventFeed, validate: createIs<EventFeedType>()},
    {name: 'nativeLandingPage', signal: this.nativeLandingPage, validate: createIs<NativeLandingPageExperiment>()},
  ];

  public async initialize() {
    if (this.platformService.isBrowser) {
      this.initializeAsync();
    }
  }

  private async initializeAsync() {
    let experiment
    try {
      experiment = Experiment.initializeWithAmplitudeAnalytics(
        'ee272489d5e5f2b06e130780e666240e'
      );
      console.time('Experiment fetch');
      await experiment.fetch();
      console.timeEnd('Experiment fetch');
    } catch (e) {
      console.error('Failed to initialize ABService', e);
    }
    this.parseVariant(experiment, 'price-option', priceOptions, this.priceOption);
    this.parseVariant(experiment, 'free-trial-option', freeTrialOptions, this.freeTrialOption);
    this.parseVariant(experiment, 'reviews-style', reviewsStyles, this.reviewsStyle);
    this.parseVariant(experiment, 'reviews', reviewPromptOptions, this.reviewPrompt);
    this.parseVariant(experiment, 'app-introduction', appIntroductionOptions, this.appIntroduction);
    this.parseVariant(experiment, 'lottery-carousel', lotteryCarouselOptions, this.lotteryCarousel);
    this.parseVariant(experiment, 'backtrack-title', backtrackTitles, this.backtrackTitle);
    this.parseVariant(experiment, 'stripe-ab-group', stripeABGroups, this.stripeABGroup);
    this.parseJsonVariantWithDefault(
      experiment,
      'advanced-benefits-page',
      this.advancedBenefitsPage,
      createIs<AdvancedBenefitsPageInput>(),
      defaultAdvancedBenefitsPage,
    );
    this.parseJsonVariantWithDefault(
      experiment,
      'benefits-page-content',
      this.benefitsPageContent,
      createIs<BenefitsPageContentInput>(),
      defaultBenefitsPageContent,
    );
    this.parseJsonVariant(experiment, 'promotion-banner-2', this.promotionHeader, createIs<PromotionHeader>());
    this.parseJsonVariant(experiment, 'event-feed-2', this.eventFeed, createIs<EventFeedType>());
    this.parseJsonVariantWithDefault(
      experiment,
      'native-landing-page',
      this.nativeLandingPage,
      createIs<NativeLandingPageExperiment>(),
      nativeLandingPageDefaults,
    );

    this.parseVariant(experiment, 'payment-location-group', paymentLocationGroups, this.paymentLocationGroup);
    this.parseVariant(experiment, 'native-payment-location-group', paymentLocationGroups, this.nativePaymentLocationGroup);
    this.parseVariant(experiment, 'native-payment', nativePaymentOptions, this.nativePayment);
    this.parseVariant(experiment, 'resubscribe-flow', resubscribeFlowOptions, this.resubscribeFlow);
  }

  private parseVariant<T>(experiment: ExperimentClient | undefined,
                          variantKey: string,
                          options: readonly T[],
                          writeableSignal: WritableSignal<T>) {
    try {
      if (!experiment) {
        return;
      }
      const override = (window as any).abOverrides;
      if (override && override[variantKey]) {
        for (const option of options) {
          if (override[variantKey] === option) {
            writeableSignal.set(override[variantKey]);
            return;
          }
        }
      }

      const variant = experiment.variant(variantKey);

      for (const option of options) {
        const value = variant.value;
        if (value === option) {
          writeableSignal.set(option);
          return;
        }
      }
    } catch (e) {
      console.error(`Failed to parse variant ${variantKey}`, e);
    }
  }

  private parseJsonVariant<T>(
    experiment: ExperimentClient | undefined,
    variantKey: string,
    writeableSignal: WritableSignal<T | undefined>,
    isFn: (input: unknown) => input is T
  ) {
    try {
      const override = (window as any).abOverrides;
      if (override && override[variantKey]) {
        if (isFn(override[variantKey])) {
          writeableSignal.set(override[variantKey]);
          return;
        }
      }

      if (!experiment) {
        return;
      }

      const variant = experiment.variant(variantKey);
      const payload = variant.payload;

      if (payload === undefined || payload === null) {
        console.error("Missing payload for JSON variant", variantKey);
        return;
      }

      if (isFn(payload)) {
        writeableSignal.set(payload);
        return;
      }

      console.error("Parsing JSON variant", variantKey, payload);
    } catch (e) {
      console.error(`Failed to parse variant ${variantKey}`, e);
    }
  }

  private parseJsonVariantWithDefault<TInput extends object, TOutput extends TInput>(
    experiment: ExperimentClient | undefined,
    variantKey: string,
    writeableSignal: WritableSignal<TOutput> | WritableSignal<TOutput | undefined>,
    isFn: (input: unknown) => input is TInput,
    defaultValue: TOutput
  ) {
    try {
      const override = (window as any).abOverrides;
      if (override && override[variantKey]) {
        const merged: TOutput = { ...defaultValue, ...override[variantKey] };
        if (isFn(merged)) {
          writeableSignal.set(merged);
          return;
        }
      }

      if (!experiment) {
        writeableSignal.set(defaultValue);
        return;
      }

      const variant = experiment.variant(variantKey);
      const payload = variant.payload;

      if (payload === undefined || payload === null) {
        writeableSignal.set(defaultValue);
        return;
      }

      const merged: TOutput = { ...defaultValue, ...payload };
      if (isFn(merged)) {
        writeableSignal.set(merged);
        return;
      }

      console.error("Parsing JSON variant", variantKey, payload);
      writeableSignal.set(defaultValue);
    } catch (e) {
      console.error(`Failed to parse variant ${variantKey}`, e);
      writeableSignal.set(defaultValue);
    }
  }
}
